import { useTheme } from "@emotion/react";
import { Box, Button, Divider, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { Stack } from "@mui/system";
import { useAppDialog } from "@src/v3.0.0/store/app-dialog";
import { useAppSwipeableDrawer } from "@src/v3.0.0/store/app-swipable-drawer";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import CustomContentEditable from "../CustomContentEditable";
import { getDataset, isNumerical } from "@src/v3.0.0/lib/utils";
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { useCropperStore } from "@src/v3.0.0/store/cropper/cropper-store";
import SquareMeters from "../SquareMeters";
import { useCropperAction } from "@src/v3.0.0/store/cropper/cropper-actions";

const dialogArgs = {
  title: "Imposta le dimensioni della tua parete"
};

const inputs = {
  width: {
    // label: "Larghezza",
    startLabel: <Typography lineHeight={1} mr={0.5} fontSize={'0.8em'}>Larghezza</Typography>,
    // startIcon: <SvgIcon component={IconDoubleArrowHoriz_}  fontSize="small" viewBox="0 0 24 24" sx={{ mr: 0.5, ml: 0.5 }}/>,
    endIcon: <Typography ml="4px" lineHeight={1} fontSize={'1em'} fontFamily={'monospace'}>cm</Typography>,
    tooltip: {
      title: "Inserisci la larghezza della tua parete",
      placement: "top",
      disableFocusListener: true,
    },
  },
  height: {
    // label: "Altezza",
    startLabel: <Typography lineHeight={1} mr={0.5} fontSize={'0.8em'}>Altezza</Typography>,
    // startIcon: <SvgIcon component={IconDoubleArrowVert_} fontSize="small" viewBox="0 0 24 24"/>,
    endIcon: <Typography ml="4px" lineHeight={1} fontSize={'1em'} fontFamily={'monospace'}>cm</Typography>,
    tooltip: {
      title: "Inserisci l'altezza della tua parete",
      placement: "top",
      disableFocusListener: true,
    },
  }
};

function PerimeterControl() {
  const theme = useTheme();
  const isLG = useMediaQuery(theme.breakpoints.up("sm"));
  const { invokeDialog } = useAppDialog();
  const { invokeSwipeableDrawer } = useAppSwipeableDrawer();

  const onClickTool = async event => {
    event.preventDefault();
    const clickedLength = getDataset(event.target, "length");
    const eventCallback = isLG ? invokeDialog : invokeSwipeableDrawer;

    let focusedEl = "width";
    if (clickedLength) {
      focusedEl = clickedLength;
    }

    try {
      await eventCallback(
        (resolve, reject, close) => (
          <Stack p={1} flex={1} justifyContent={'center'} alignItems={'center'}>
            <PerimeterControl_ isFocused={focusedEl} tooltip={false} onSubmit={close} />
            <Divider />
            <Button fullWidth endIcon={<KeyboardReturnIcon />} variant="contained" color="success" onClick={close}>
              Applica misure
            </Button>
          </Stack>
        ),
        dialogArgs
      );
    } catch(e) {}
  };

  return (
    // <div onClick={onClickTool}>
      <PerimeterControl_ allowFocus={true} />
    // </div>
  );
};

function PerimeterControl_(props) {
  const { allowFocus = true, onSubmit, isFocused } = props;
  const { preset, updatePreset } = useCropperStore();
  const {dispatch: dispatchCropperAction} = useCropperAction();
  const [perimeter, setPerimeter] = useState(() => ({
    width: String(preset.width),
    height: String(preset.height),
  }));
  
  const handleKeyDown = useCallback((event) => {

    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit && onSubmit(event);
    }

    else if(event.key === "ArrowUp" || event.key === "ArrowDown") {
      const increment = event.key === "ArrowUp" ? 1 : -1;
      event.preventDefault();
      const key = getDataset(event.currentTarget, 'length');
      setPerimeter((state) => ({ ...state, [key]: parseInt(perimeter[key]) + increment }));  
    }

    // lastPressedKey.current = event.key;
  }, [perimeter, setPerimeter]);

  const handleChange = (event) => {
    let value = event.target.value;
    if (!isNumerical(value)) value = '';
    const key = getDataset(event.currentTarget, 'length');
    setPerimeter((state) => ({ ...state, [key]: value }));
  };

  const handleBlur = (event) => {
    if (event.target.textContent === "") {
      const key = getDataset(event.currentTarget, 'length');
      setPerimeter((prevPerimeter) => ({
        ...prevPerimeter,
        [key]: String(preset[key]),
      }));
    }
  };

  useEffect(() => {
    if (Number(preset.width) !== Number(perimeter.width)
    || Number(preset.height) !== Number(perimeter.height)) {
      setPerimeter({
        width: parseFloat(preset.width),
        height: parseFloat(preset.height),
      });
    }
  }, [preset]);

  useEffect(() => {

    if (!isNumerical(perimeter.width) || !isNumerical(perimeter.height)) {
      return;
    }

    if (Number(perimeter.width) !== Number(preset.width)
    || Number(perimeter.height) !== Number(preset.height)) { 
      const newPerimeter = {
        width: Number(perimeter.width),
        height: Number(perimeter.height),
      };

      updatePreset(newPerimeter, false);
      dispatchCropperAction({ perimeter: newPerimeter });
    }
  }, [perimeter]);

  return (
    <Stack direction={'row'} alignItems={'center'} gap={0.25}>
      {Object.keys(inputs).map((size, key) => (
        <Fragment key={key}>
          <Tooltip {...inputs[size].tooltip}>
            <span>
              <CustomContentEditable 
                isNumerical
                className="cp-perimeter-ipt"
                value={perimeter[size]}
                startLabel={inputs[size]?.startLabel}
                startIcon={inputs[size]?.startIcon}
                endIcon={inputs[size].endIcon}
                allowFocus={allowFocus}
                isFocused={isFocused === size}
                slotProps={{
                  contentEditable: {
                    'data-length': size,
                    onChange: handleChange,
                    onKeyDown: handleKeyDown,
                    onBlur: handleBlur,
                  }
                }}
              />
            </span>
          </Tooltip>
          <Box mr={1} />
        </Fragment>
      ))}
      <SquareMeters />
    </Stack>
  );
};

export default PerimeterControl;